import Loader from "pages/Separate/Loader"
import React, { useEffect, useMemo, useState } from "react"
import secureLocalStorage from "react-secure-storage"
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData"
import {
  Table,
  Row,
  Col,
  Modal,
  Spinner,
  NavItem,
  Nav,
  NavLink,
  TabPane,
  TabContent,
} from "reactstrap"
import { useTable, usePagination, useGlobalFilter } from "react-table"
import NewPagination from "components/Common/Newpagination"
import moment from "moment"
import Swal from "sweetalert2"
import configure from "configure"
import classnames from "classnames"
import PersonalDetails from "pages/Staff/ViewStaff/PersonalDetails"
import ZithasCtc from "pages/Staff/ViewStaff/ZithasCtc"
import WorkExperience from "pages/Staff/ViewStaff/WorkExperience"
import Education from "pages/Staff/ViewStaff/Education"
import Documents from "pages/Staff/ViewStaff/Document"
import GenerateDoc from "pages/Staff/ViewStaff/GenerateDoc"
import ZithasCredential from "pages/Staff/ViewStaff/ZithasCredential"
import PermissionView from "pages/Staff/ViewStaff/Permisions"
import CompanyCommitment from "pages/Staff/ViewStaff/CompnayCommitment"
import { getInitials } from "utils/common-helpers"

// ─── Table Container ────────────────────────────────────────────────────────
function TableContainer({ columns, data }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable({ columns, data }, useGlobalFilter, usePagination)

  const { pageIndex, pageSize, globalFilter } = state

  return (
    <div className="px-2">
      <Row className="mb-2 align-items-center">
        <Col lg={2}>
          <select
            className="border-radius form-select"
            style={{ width: "100px" }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 40, 50].map(s => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </Col>
        <Col className="d-flex justify-content-end gap-3 align-items-center">
          <div className="search-box d-inline-block">
            <div className="position-relative">
              <input
                type="text"
                className="border-radius form-control rounded"
                placeholder="Search..."
                value={globalFilter || ""}
                onChange={e => setGlobalFilter(e.target.value)}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
        </Col>
      </Row>

      <Table className="table table-bordered mt-4" {...getTableProps()}>
        <thead>
          {headerGroups.map((hg, i) => (
            <tr key={i} {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col, j) => (
                <th key={j} className={col.className} {...col.getHeaderProps()}>
                  {col.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr className="row-hover" key={i} {...row.getRowProps()}>
                {row.cells.map((cell, j) => (
                  <td
                    key={j}
                    className={cell.column.className}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            )
          })}
          {page.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="text-center text-muted py-4"
              >
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <Row className="justify-content-md-end justify-content-center align-items-center">
        <NewPagination
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          previousPage={previousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          page={page}
          data={data}
          nextPage={nextPage}
        />
      </Row>
    </div>
  )
}

// ─── Currency Formatter ──────────────────────────────────────────────────────
const fmt = amount =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0)

// ─── Main Page ───────────────────────────────────────────────────────────────
const CandidateApproval = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [approving, setApproving] = useState(false)
  const [letterTemplate, setLetterTemplate] = useState({})
  const token = secureLocalStorage.getItem("auth_token")

  const getApprovalRequests = async () => {
    const res = await fetchNodeData(
      setLoading,
      `employeeJoining/pendingApprovalsList`,
      token
    )
    if (res?.success) setData(res?.users || [])
  }

  useEffect(() => {
    getApprovalRequests()
  }, [])

  useEffect(() => {
    ;(async () => {
      try {
        const response = await fetchNodeData(
          false,
          `staffTemplate/21`,
          secureLocalStorage.getItem("auth_token")
        )
        setLetterTemplate(response.data || {})
      } catch (error) {
        console.log(error)
      }
    })()
  }, [])

  const handleView = employee => {
    setSelectedEmp(employee)
    setModalOpen(true)
  }

  const handleClose = () => {
    setModalOpen(false)
    setSelectedEmp(null)
  }

  const handleApprove = async employee => {
    console.log(employee)
    const result = await Swal.fire({
      icon: "warning",
      title: "Approve Employee?",
      text: `Confirm onboarding approval for ${employee.name}.`,
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
    })

    if (result.isConfirmed) {
      setApproving(true)
      const payload = {
        id: employee.id,
      }
      try {
        const apiRes = await FetchNodePostData(
          false,
          "employeeJoining/finalApproval",
          token,
          payload
        )

        if (apiRes?.data?.success) {
          const formData = new FormData()
          formData.append("staff_id", employee.id)
          formData.append("letterType", letterTemplate?.type || "")
          formData.append("letterTemplate", letterTemplate?.name || "")
          formData.append("template_format", letterTemplate?.description || "")
          formData.append("signature", employee?.signature || "")
          formData.append("isAppointmentLetter", 1)

          await FetchNodePostData(
            false,
            "generateStaffLetter",
            secureLocalStorage.getItem("auth_token"),
            formData
          )

          handleClose()
          getApprovalRequests() // refresh list
          Swal.fire({
            icon: "success",
            text: "Approved Successfully",
          })
        }
      } catch (err) {
        Swal.fire("Error", "Something went wrong. Please try again.", "error")
      } finally {
        setApproving(false)
      }
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        className: "text-center",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Emp ID",
        accessor: "employee_code",
        className: "text-start",
      },
      {
        Header: "Name",
        accessor: "name",
        className: "text-start",
        Cell: ({ row }) => {
          return (
            <div className="d-flex gap-2 align-items-center">
              {row?.original?.avatar ? (
                <img
                  src={`${configure.imgUrl}${row?.original?.avatar}`}
                  alt="Avatar"
                  title={row?.original?.name} // tooltip for hover
                  className="img-fluid rounded-circle"
                  width={25}
                  height={25}
                />
              ) : (
                <div
                  className="defualt-img rounded-circle d-flex align-items-center justify-content-center"
                  title={row?.original?.name}
                  style={{
                    width: 25,
                    height: 25,
                    fontSize: 10,
                  }}
                >
                  {getInitials(row?.original?.name)}
                </div>
              )}
              {row?.original?.name || "N/A"}
            </div>
          )
        },
      },
      {
        Header: "Email",
        accessor: "email",
        className: "text-start",
      },
      {
        Header: "Punch ID",
        accessor: "punchid",
        className: "text-start",
      },
      {
        Header: "Join Date",
        accessor: "date_of_joining",
        className: "text-start",
        Cell: ({ row }) =>
          moment(row.original.date_of_joining).format("DD-MM-YYYY"),
      },
      {
        Header: "Salary",
        accessor: "salary",
        className: "text-start",
        Cell: ({ row }) => {
          const {
            basic_pay,
            dearness_allowance,
            medical_allowance,
            house_rent_allowance,
            conveyance_allowance,
          } = row.original
          const total = [
            basic_pay,
            dearness_allowance,
            medical_allowance,
            house_rent_allowance,
            conveyance_allowance,
          ].reduce((s, v) => s + Number(v || 0), 0)
          return fmt(total)
        },
      },
      {
        Header: "Action",
        accessor: "action",
        className: "text-center",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handleView(row.original)}
          >
            <i className="fas fa-eye me-1"></i> View
          </button>
        ),
      },
    ],
    []
  )

  return (
    <>
      {loading && <Loader />}

      <div>
        <div className="mb-4 mt-2 d-flex justify-content-between">
          <p className="text-purple fs-5 mb-0 fw-bold">Candidate Approval</p>
        </div>

        <div className="responsive-table">
          <TableContainer columns={columns} data={data} />
        </div>
      </div>

      <StaffModal
        modal_fullscreen={modalOpen}
        setmodal_fullscreen={setModalOpen}
        filterViewStaffData={selectedEmp}
        onApprove={handleApprove}
        approving={approving}
      />
    </>
  )
}

function StaffModal({
  modal_fullscreen,
  setmodal_fullscreen,
  filterViewStaffData,
  onApprove,
  approving,
}) {
  const [verticalActiveTab, setverticalActiveTab] = useState("1")
  const toggleVertical = tab => {
    setverticalActiveTab(tab)
  }

  const [staffData, setStaffData] = useState({})

  const getStaffData = item => {
    setStaffData(item)
  }

  return (
    <Modal
      size="xl"
      isOpen={modal_fullscreen}
      toggle={() => {
        setmodal_fullscreen(false)
      }}
      className="modal-fullscreen"
    >
      <div className="modal-header mb-0">
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ width: "100%" }}
        >
          <h5 className="col-6 modal-title mt-0">Staff Details</h5>

          <div className="col-6 mr-0 d-flex justify-content-end align-items-center">
            <button
              type="button"
              disabled={approving}
              onClick={() => onApprove(filterViewStaffData)}
              className=" btn btn-success fw-semibold rounded me-5"
            >
              {approving ? (
                <Spinner size="sm" className="me-2" />
              ) : (
                <i className="fas fa-check-circle me-2"></i>
              )}
              Approve
            </button>

            {filterViewStaffData?.avatar ? (
              <img
                src={`${configure.imgUrl}${filterViewStaffData?.avatar}`}
                className="profileImg"
                alt="no img"
              />
            ) : (
              <div
                className="defualt-img rounded-circle d-flex align-items-center justify-content-center"
                title={filterViewStaffData?.name}
                style={{
                  width: 37,
                  height: 37,
                  fontSize: 14,
                }}
              >
                {getInitials(filterViewStaffData?.name)}
              </div>
            )}

            <p className="fw-bold mb-0 ms-2">{filterViewStaffData?.name}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setmodal_fullscreen(false)
            setverticalActiveTab("1")
          }}
          className="ms-4"
          style={{ border: "none", backgroundColor: "transparent" }}
        >
          <span
            aria-hidden="true"
            style={{ color: "#bf1e30", fontSize: "28px", lineHeight: "1px" }}
          >
            {" "}
            &times;{" "}
          </span>
        </button>
      </div>
      <div
        className="modal-body hide-bottom-scrollbar ps-3 pe-0 pt-0"
        id="staff-View"
      >
        <Row className="gx-4 justify-content-between">
          <Col md="2" className="side-tab py-3 h-100 bd-fixed-sidetab">
            <Nav pills className="flex-column">
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "1",
                  })}
                  onClick={() => {
                    toggleVertical("1")
                  }}
                >
                  Personal Details
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "2",
                  })}
                  onClick={() => {
                    toggleVertical("2")
                  }}
                >
                  Zithas CTC
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "3",
                  })}
                  onClick={() => {
                    toggleVertical("3")
                  }}
                >
                  Work Experience
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "4",
                  })}
                  onClick={() => {
                    toggleVertical("4")
                  }}
                >
                  Education
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "5",
                  })}
                  onClick={() => {
                    toggleVertical("5")
                  }}
                >
                  Documents
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "8",
                  })}
                  onClick={() => {
                    toggleVertical("8")
                  }}
                >
                  Generate Documents
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "6",
                  })}
                  onClick={() => {
                    toggleVertical("6")
                  }}
                >
                  Zithas Credential
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "9",
                  })}
                  onClick={() => {
                    toggleVertical("9")
                  }}
                >
                  Company Commitment
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    "mb-2": true,
                    active: verticalActiveTab === "7",
                  })}
                  onClick={() => {
                    toggleVertical("7")
                  }}
                >
                  Permission
                </NavLink>
              </NavItem>
            </Nav>
          </Col>
          <Col md="10" className="bd-right-side-tab">
            <div className="p-4 h-100 pl-0">
              <TabContent
                activeTab={verticalActiveTab}
                className="text-muted mt-4 mt-md-0"
              >
                <TabPane tabId="1">
                  <PersonalDetails
                    id={filterViewStaffData?.id}
                    verticalActiveTab={verticalActiveTab}
                    staffData={getStaffData}
                  />
                </TabPane>
                <TabPane tabId="2">
                  <ZithasCtc
                    id={filterViewStaffData?.id}
                    zithasCTC={staffData}
                  />
                </TabPane>
                <TabPane tabId="3">
                  <WorkExperience
                    id={filterViewStaffData?.id}
                    staffData={staffData}
                    verticalActiveTab={verticalActiveTab}
                  />
                </TabPane>
                <TabPane tabId="4">
                  <Education
                    id={filterViewStaffData?.id}
                    staffData={staffData}
                    verticalActiveTab={verticalActiveTab}
                  />
                </TabPane>
                <TabPane tabId="5">
                  <Documents
                    id={filterViewStaffData?.id}
                    verticalActiveTab={verticalActiveTab}
                  />
                </TabPane>
                <TabPane tabId="8">
                  <GenerateDoc
                    id={filterViewStaffData?.id}
                    verticalActiveTab={verticalActiveTab}
                  />
                </TabPane>
                <TabPane tabId="6">
                  <ZithasCredential id={filterViewStaffData?.id} />
                </TabPane>
                <TabPane tabId="7">
                  <PermissionView
                    id={filterViewStaffData?.id}
                    verticalActiveTab={verticalActiveTab}
                  />
                </TabPane>
                <TabPane tabId="9">
                  <CompanyCommitment
                    id={filterViewStaffData?.id}
                    staffData={staffData}
                  />
                </TabPane>
              </TabContent>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  )
}

export default CandidateApproval
